import {
  type Automation,
  getAutomationPlaybookIds,
  getAutomationTemplateById,
} from "@repo/app-types/entities/automation";
import type { PlaybookId } from "@repo/app-types/entities/playbook";
import { Switch } from "@repo/ui/Switch/switch";
import {
  EntityTable,
  type EntityTableColumnDefinition,
} from "@repo/ui/tables/entityTable";
import type { FC, ReactNode } from "react";
import { useMemo } from "react";
import { automationTemplateIcons } from "./automationTemplateIcons.js";
import { describeAutomationTrigger } from "./automationTriggerSummary.js";

export interface AutomationsTableProps {
  automations: readonly Automation[] | undefined;
  isLoading: boolean;
  /**
   * Renders the enable switches disabled for viewers who cannot modify automations
   * (reviewers). Rows stay activatable so they can still open each automation.
   */
  isReadOnly?: boolean;
  playbookNamesById: ReadonlyMap<PlaybookId, string>;
  emptyMessage: ReactNode;
  /** Called when the user activates a row, e.g. to navigate to the automation. */
  onAutomationAction: (automation: Automation) => void;
  onToggleEnabled: (automation: Automation, isEnabled: boolean) => void;
}

export const AutomationsTable: FC<AutomationsTableProps> = (props) => {
  const {
    automations,
    isLoading,
    isReadOnly,
    playbookNamesById,
    emptyMessage,
    onAutomationAction,
    onToggleEnabled,
  } = props;
  const columns = useMemo<readonly EntityTableColumnDefinition<Automation>[]>(
    () => [
      {
        id: "automation",
        header: "Automation",
        isRowHeader: true,
        getTextValue: (automation) => automation.name,
        renderCell: (automation) => {
          const Icon = automationTemplateIcons[automation.template_id];
          const playbookName =
            getAutomationPlaybookIds(automation.settings)
              .map((playbookId) => playbookNamesById.get(playbookId))
              .filter(Boolean)
              .join(", ") || undefined;
          return (
            <span className="flex min-w-0 items-center gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 ring-1 ring-indigo-100">
                <Icon aria-hidden className="size-5" />
              </span>
              {/* `w-0 flex-auto` keeps the nowrap name/playbook text out of
                  the table's intrinsic width, so it truncates instead of
                  stretching the column. */}
              <span className="w-0 flex-auto">
                <span className="block truncate font-medium text-foreground">
                  {automation.name}
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                  {playbookName ??
                    getAutomationTemplateById(automation.template_id).name}
                </span>
              </span>
            </span>
          );
        },
      },
      {
        id: "trigger",
        header: "Trigger",
        // Shrink to the schedule text, which never wraps, leaving the surplus
        // width to the automation column.
        className: "w-0",
        getTextValue: (automation) => describeAutomationTrigger(automation),
        renderCell: (automation) => (
          <span className="whitespace-nowrap text-muted-foreground">
            {describeAutomationTrigger(automation)}
          </span>
        ),
      },
      {
        id: "enable",
        header: "Enable",
        className: "w-24",
        renderCell: (automation) => (
          <Switch
            isSelected={automation.is_enabled}
            isDisabled={isReadOnly}
            onChange={(isEnabled) => {
              onToggleEnabled(automation, isEnabled);
            }}
            accessibleLabel={`Enable ${automation.name}`}
          />
        ),
      },
    ],
    [playbookNamesById, onToggleEnabled, isReadOnly],
  );

  return (
    // Always rendered inside a `TableCard`, so the table itself is full bleed.
    <EntityTable
      aria-label="Automations"
      columns={columns}
      entities={automations}
      isLoading={isLoading}
      isFullBleed
      emptyMessage={emptyMessage}
      onEntityAction={onAutomationAction}
    />
  );
};
